import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Clock } from 'lucide-react';
import { requirementsService, type RequirementListItem } from '@/services/requirements.service';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';

const PRIORITY_BADGE: Record<string, string> = {
  critical: 'bg-rose-100 text-rose-700 border-rose-200',
  high: 'bg-orange-100 text-orange-700 border-orange-200',
  medium: 'bg-teal-100 text-teal-700 border-teal-200',
  low: 'bg-emerald-100 text-emerald-700 border-emerald-200',
};

function InfoRow({ label, value }: { label: string; value?: string | number | null }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm font-medium">{value ?? '—'}</span>
    </div>
  );
}

interface Props {
  open: boolean;
  requirement: RequirementListItem | null;
  onClose: () => void;
}

export function RequirementDetailDrawer({ open, requirement, onClose }: Props) {
  const { data: detail, isLoading } = useQuery({
    queryKey: ['requirements', 'detail', requirement?.id],
    queryFn: () => requirementsService.getRequirement(requirement!.id),
    enabled: open && !!requirement,
  });

  const formatCurrency = (v: number | null | undefined) =>
    v != null ? `₹${v.toLocaleString('en-IN')}` : '—';

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-full max-w-3xl max-h-[90vh] overflow-y-auto" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="sr-only">Requirement Detail</DialogTitle>
        </DialogHeader>

        {isLoading || !detail ? (
          <div className="space-y-4 py-4">
            <div className="h-8 w-3/4 animate-pulse rounded bg-muted/60" />
            <div className="grid grid-cols-2 gap-4">
              {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-10 animate-pulse rounded bg-muted/60" />)}
            </div>
            <div className="h-24 w-full animate-pulse rounded bg-muted/60" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-wrap items-start gap-3">
              <div className="flex-1 min-w-0">
                <h2 className="text-xl font-semibold leading-tight">{detail.title}</h2>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Badge
                  variant="outline"
                  className={`capitalize ${PRIORITY_BADGE[detail.priority] ?? ''}`}
                >
                  {detail.priority}
                </Badge>
                {detail.status && (
                  <span
                    className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium border"
                    style={{ borderColor: `${detail.status.color}40`, color: detail.status.color, backgroundColor: `${detail.status.color}18` }}
                  >
                    <span className="h-1.5 w-1.5 rounded-full inline-block" style={{ backgroundColor: detail.status.color }} />
                    {detail.status.name}
                  </span>
                )}
              </div>
            </div>

            {/* Info grid */}
            <div className="grid grid-cols-2 gap-x-6 gap-y-4 rounded-lg border border-white/30 bg-white/30 p-4 backdrop-blur-sm sm:grid-cols-3">
              <InfoRow label="Category" value={detail.category?.name} />
              <InfoRow label="Requested By" value={detail.requested_by?.name} />
              <InfoRow label="Assigned To" value={detail.assigned_to?.name} />
              <InfoRow label="Quantity" value={detail.quantity != null ? `${detail.quantity} ${detail.unit}` : undefined} />
              <InfoRow label="Estimated Cost" value={formatCurrency(detail.estimated_cost)} />
              <InfoRow label="Approved Budget" value={formatCurrency(detail.approved_budget)} />
              <InfoRow label="Due Date" value={detail.due_date ? format(new Date(detail.due_date), 'dd MMM yyyy') : null} />
              <InfoRow label="Fulfilled Date" value={detail.fulfilled_date ? format(new Date(detail.fulfilled_date), 'dd MMM yyyy') : null} />
              <InfoRow label="Created" value={format(new Date(detail.created_at), 'dd MMM yyyy')} />
            </div>

            {/* Description */}
            {detail.description && (
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Description</p>
                <p className="text-sm whitespace-pre-wrap">{detail.description}</p>
              </div>
            )}

            {/* Notes */}
            {detail.notes && (
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Notes</p>
                <p className="text-sm whitespace-pre-wrap">{detail.notes}</p>
              </div>
            )}

            {/* Rejection reason */}
            {detail.rejection_reason && (
              <div className="rounded-md border border-rose-200 bg-rose-50 p-3">
                <p className="text-xs font-medium text-rose-700 mb-1">Rejection Reason</p>
                <p className="text-sm text-rose-700">{detail.rejection_reason}</p>
              </div>
            )}

            {/* Audit Log */}
            {detail.audit_log.length > 0 && (
              <div className="space-y-3">
                <div className="border-t border-border/40" />
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  Activity Log
                </div>
                <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
                  {detail.audit_log.map((entry) => (
                    <div key={entry.id} className="flex gap-3 text-sm">
                      <div className="mt-1 h-2 w-2 rounded-full bg-teal-500 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <span className="font-medium">{entry.changed_by_name}</span>
                        {' '}
                        <span className="text-muted-foreground">
                          {entry.action === 'created' && 'created this requirement'}
                          {entry.action === 'deleted' && 'deleted this requirement'}
                          {entry.action === 'status_changed' && `changed status to "${entry.new_value}"`}
                          {entry.action === 'updated' && entry.field_name && (
                            <>changed <span className="font-medium">{entry.field_name}</span>{entry.old_value && ` from "${entry.old_value}"`} to &ldquo;{entry.new_value}&rdquo;</>
                          )}
                        </span>
                        {entry.note && <p className="text-xs text-muted-foreground mt-0.5">{entry.note}</p>}
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {format(new Date(entry.created_at), 'dd MMM yyyy, HH:mm')}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
